import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  TitleMedium,
  ParagraphMediumText,
  ParagraphSmallText,
} from '../../../components/Texts';
import Button from '../../../components/Button';
import { useGetMyGiftCardsQuery } from '../../../redux/api/authorized/globetopper';
import { PHONE_MEDIA_QUERY } from '../../../common/responsive';
import { getCurrencySymbol } from '../../../common/currency';
import CardsSkeleton from '../../../components/Cards/CardsSkeleton';
import { NavigationBackButton } from '../../../components/BackButton/NavigationBackButton';
import { useSelector } from 'react-redux';
import { selectPrivyToken } from '../../../redux/slices/auth';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';

const Container = styled.div`
  width: 100%;
  padding: 20px;
  padding-bottom: 50px;
`;

const Header = styled.div`
  margin-bottom: 24px;
`;

const GridContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 16px;
  }
`;

const CardContainer = styled.div`
  background-color: ${({ theme }) => theme.background.primary};
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.border.primary};
  display: flex;
  flex-direction: row;
  overflow: hidden;
  transition: transform 0.3s ease;
  @media (hover: hover) {
    &:hover {
      transform: translateY(-5px);
    }
  }
  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
  }
`;

const CardImageContainer = styled.div`
  width: 40%;
  min-width: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.background.secondary || '#f5f5f5'};
  padding: 40px;
  @media ${PHONE_MEDIA_QUERY} {
    width: 100%;
    min-width: unset;
    height: 200px;
    padding: 16px;
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  max-height: 500px;
  object-fit: contain;
  @media ${PHONE_MEDIA_QUERY} {
    max-height: 100%;
  }
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 32px;
  flex: 1;
  @media ${PHONE_MEDIA_QUERY} {
    padding: 20px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BrandName = styled(TitleMedium)`
  color: ${({ theme }) => theme.text.title};
  margin: 0;
`;

const AmountText = styled(ParagraphMediumText)`
  color: ${({ theme }) => theme.text.regular};
  font-weight: 600;
  margin: 0;
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.border.secondary};
`;

const InfoLabel = styled(ParagraphSmallText)`
  color: ${({ theme }) => theme.text.secondary};
  font-weight: 500;
`;

const InfoValue = styled(ParagraphSmallText)`
  color: ${({ theme }) => theme.text.regular};
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  gap: 16px;
`;

const EmptyStateText = styled(ParagraphMediumText)`
  color: ${({ theme }) => theme.text.secondary};
`;

const ErrorMessage = styled.div`
  padding: 20px;
  text-align: center;
  color: ${({ theme }) => theme.text.error || theme.text.secondary};
`;

const getFormatDate = (t, locale) => (dateString) => {
  if (!dateString) return t('spend.myGiftCards.dateNa');
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString(locale || 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
};

// Helper to check if date is expired
const isExpired = (expirationDate) => {
  if (!expirationDate) return false;
  try {
    const expDate = new Date(expirationDate);
    return expDate < new Date();
  } catch {
    return false;
  }
};

const GiftCardItem = ({ giftCard, formatDate }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const name = giftCard.name;
  const imageUrl = giftCard.imageUrl;
  const localAmount = giftCard.localAmount;
  const currencySymbol = getCurrencySymbol(giftCard.currency);
  const status = giftCard.status;
  const expirationDate = giftCard.expirationDate;
  const redemptionUrl = giftCard.redemptionUrl;

  const expired = isExpired(expirationDate);

  const handleRedeem = () => {
    if (
      Capacitor.isNativePlatform() &&
      Capacitor.isPluginAvailable('Browser')
    ) {
      Browser.open({
        url: redemptionUrl,
        presentationStyle: 'popover',
      });
    } else {
      window.open(redemptionUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <CardContainer>
      {imageUrl && (
        <CardImageContainer>
          <CardImage
            src={imageUrl}
            alt={name}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </CardImageContainer>
      )}
      <CardBody>
        <CardHeader>
          <BrandName>{name}</BrandName>
          <AmountText>
            {currencySymbol}
            {Number(localAmount).toFixed(2)}
          </AmountText>
        </CardHeader>

        <InfoRow>
          <InfoLabel>{t('spend.myGiftCards.status')}</InfoLabel>
          <InfoValue
            style={{
              color:
                status === 'PAID'
                  ? theme.status?.success?.primary || theme.primary.primary
                  : theme.text.secondary,
            }}
          >
            {status === 'PAID' ? t('spend.myGiftCards.statusPaid') : status}
          </InfoValue>
        </InfoRow>

        <InfoRow>
          <InfoLabel>{t('spend.myGiftCards.expires')}</InfoLabel>
          <InfoValue
            style={{
              color: expired
                ? theme.text.error || theme.status?.error?.primary
                : theme.text.regular,
            }}
          >
            {formatDate(expirationDate)}
            {expired && t('spend.myGiftCards.expiredSuffix')}
          </InfoValue>
        </InfoRow>

        <InfoRow>
          <InfoLabel>{t('spend.myGiftCards.email')}</InfoLabel>
          <InfoValue>{giftCard.email}</InfoValue>
        </InfoRow>

        <ButtonContainer>
          <Button
            variant='primary'
            fullWidth
            onClick={handleRedeem}
            disabled={expired}
          >
            {expired ? t('spend.myGiftCards.expired') : t('spend.myGiftCards.redeemGiftCard')}
          </Button>
        </ButtonContainer>
      </CardBody>
    </CardContainer>
  );
};

const MyGiftCards = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const privyToken = useSelector(selectPrivyToken);
  const formatDate = getFormatDate(t, i18n.language === 'es' ? 'es-ES' : 'en-US');

  const {
    data: giftCards,
    isLoading,
    error,
  } = useGetMyGiftCardsQuery(undefined, {
    skip: !privyToken,
    refetchOnMountOrArgChange: true,
  });

  if (isLoading) {
    return (
      <Container>
        <NavigationBackButton onClick={() => navigate('/app/spend')} />
        <Header>
          <TitleMedium style={{ color: theme.text.title }}>
            {t('spend.myGiftCards.title')}
          </TitleMedium>
        </Header>
        <CardsSkeleton />
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <NavigationBackButton onClick={() => navigate('/app/spend')} />
        <Header>
          <TitleMedium style={{ color: theme.text.title }}>
            {t('spend.myGiftCards.title')}
          </TitleMedium>
        </Header>
        <ErrorMessage>
          {t('spend.myGiftCards.loadError')}
        </ErrorMessage>
      </Container>
    );
  }

  if (!giftCards || giftCards.length === 0) {
    return (
      <Container>
        <NavigationBackButton onClick={() => navigate('/app/spend')} />
        <Header>
          <TitleMedium style={{ color: theme.text.title }}>
            {t('spend.myGiftCards.title')}
          </TitleMedium>
        </Header>
        <EmptyState>
          <EmptyStateText>
            {t('spend.myGiftCards.emptyState')}
          </EmptyStateText>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <NavigationBackButton onClick={() => navigate('/app/spend')} />
      <Header>
        <TitleMedium style={{ color: theme.text.title }}>
          {t('spend.myGiftCards.title')}
        </TitleMedium>
      </Header>
      <GridContainer>
        {giftCards.map((giftCard) => (
          <GiftCardItem key={giftCard.id} giftCard={giftCard} formatDate={formatDate} />
        ))}
      </GridContainer>
    </Container>
  );
};

export default MyGiftCards;
